/**
 * useRewardPopup - 奖励弹窗 Hook
 *
 * 负责奖励收集、优先级判断、弹窗展示和 localStorage 缓存。
 */

import { ref, type Ref } from 'vue-demi';
import type { Milestone } from './useMilestones';

/**
 * 待处理奖励
 */
export interface PendingReward {
  /** 奖励类型：里程碑或打卡积分 */
  type: 'milestone' | 'points';
  /** 里程碑天数或积分值 */
  value: number;
  /** 流水记录 ID，用于去重 */
  journalId: string;
  /** 奖励金额 */
  reward?: number;
}

/**
 * useRewardPopup Hook 配置
 */
export interface UseRewardPopupOptions {
  /** 用户 ID */
  uid?: number;
  /** 获取里程碑奖励金额的函数 */
  getMilestoneReward: (days: number) => number;
  /** 里程碑达成事件回调 */
  onMilestoneReward?: (days: number, reward: number) => void;
  /** 打卡奖励事件回调 */
  onCheckinReward?: (reward: number) => void;
}

/**
 * useRewardPopup Hook 返回值
 */
export interface UseRewardPopupReturn {
  /** 待处理奖励列表 */
  pendingRewards: Ref<PendingReward[]>;
  /** 本次会话已弹出的最高里程碑值 */
  shownMilestoneValue: Ref<number>;
  /** 收集待处理奖励 */
  collectPendingRewards: (
    journalEntries: JournalEntry[],
    milestones: Milestone[],
    maxStreak: number,
  ) => void;
  /** 展示最高优先级的奖励 */
  showHighestPriorityReward: () => void;
  /** 检查奖励弹窗是否已展示过 */
  hasShownRewardPopup: (journalId: string) => boolean;
  /** 标记奖励弹窗已展示 */
  markRewardPopupShown: (journalId: string) => void;
}

/**
 * 流水条目接口（简化版）
 */
export interface JournalEntry {
  id: string;
  targetId?: string;
  actionContext?: Record<string, unknown>;
}

/** localStorage 缓存前缀 */
const REWARD_POPUP_CACHE_PREFIX = 'ark_reward_popup_';

/**
 * 获取奖励优先级
 * 里程碑优先级 = 里程碑天数，打卡优先级 = 0
 */
export function getRewardPriority(reward: PendingReward): number {
  if (reward.type === 'milestone') {
    return reward.value;
  }
  return 0;
}

/**
 * 奖励弹窗 Hook
 */
export function useRewardPopup(
  options: UseRewardPopupOptions,
): UseRewardPopupReturn {
  const { uid, getMilestoneReward, onMilestoneReward, onCheckinReward } =
    options;

  const pendingRewards = ref<PendingReward[]>([]);
  const shownMilestoneValue = ref<number>(0);

  /**
   * 检查奖励弹窗是否已展示过
   */
  function hasShownRewardPopup(journalId: string): boolean {
    try {
      return (
        localStorage.getItem(`${REWARD_POPUP_CACHE_PREFIX}${journalId}`) ===
        'shown'
      );
    } catch {
      return false;
    }
  }

  /**
   * 标记奖励弹窗已展示
   */
  function markRewardPopupShown(journalId: string): void {
    try {
      localStorage.setItem(`${REWARD_POPUP_CACHE_PREFIX}${journalId}`, 'shown');
    } catch {
      console.warn('localStorage 不可用');
    }
  }

  /**
   * 收集待处理奖励
   */
  function collectPendingRewards(
    journalEntries: JournalEntry[],
    milestones: Milestone[],
    maxStreak: number,
  ): void {
    console.log('[奖励弹窗] collectPendingRewards 开始', {
      journalEntriesCount: journalEntries.length,
      milestonesCount: milestones.length,
      maxStreak,
    });

    const rewards: PendingReward[] = [];
    const milestoneDays = milestones.map((m) => m.days);

    // 从流水中收集奖励
    for (const entry of journalEntries) {
      const alreadyShown = hasShownRewardPopup(entry.id);
      console.log('[奖励弹窗] 检查流水条目', {
        entryId: entry.id,
        targetId: entry.targetId,
        alreadyShown,
      });

      if (alreadyShown) continue;

      const context = entry.actionContext || {};
      const actionCode = (context.actionCode as string) || '';

      // 打卡奖励
      if (actionCode === 'INTERACT' || entry.targetId) {
        console.log('[奖励弹窗] 添加打卡奖励', {
          entryId: entry.id,
          targetId: entry.targetId,
        });
        rewards.push({
          type: 'points',
          value: 1,
          journalId: entry.id,
          reward: 10,
        });
      }

      // 里程碑奖励（从流水上下文中获取）
      if (actionCode === 'milestone_reached' || context.milestone_value) {
        const milestoneValue =
          (context.milestone_value as number) || (context.value as number);
        if (milestoneValue && milestoneDays.includes(milestoneValue)) {
          rewards.push({
            type: 'milestone',
            value: milestoneValue,
            journalId: entry.id,
            reward: getMilestoneReward(milestoneValue),
          });
        }
      }
    }

    // 根据当前进度补充里程碑奖励
    for (const milestone of milestoneDays) {
      if (maxStreak >= milestone) {
        const cacheKey = `milestone_${milestone}_${uid}`;
        if (!hasShownRewardPopup(cacheKey)) {
          rewards.push({
            type: 'milestone',
            value: milestone,
            journalId: cacheKey,
            reward: getMilestoneReward(milestone),
          });
        }
      }
    }

    console.log('[奖励弹窗] collectPendingRewards 完成', {
      rewardsCount: rewards.length,
      rewards,
    });
    pendingRewards.value = rewards;
  }

  /**
   * 展示最高优先级的奖励
   */
  function showHighestPriorityReward(): void {
    if (pendingRewards.value.length === 0) return;

    // 按优先级降序排列
    const sorted = [...pendingRewards.value].sort(
      (a, b) => getRewardPriority(b) - getRewardPriority(a),
    );

    const highest = sorted[0];
    if (!highest) return;

    // 将所有待处理奖励标记为已展示
    for (const reward of pendingRewards.value) {
      markRewardPopupShown(reward.journalId);
    }

    // 展示最高优先级的奖励
    if (highest.type === 'milestone') {
      // 检查是否已弹出过相同或更高里程碑值
      if (highest.value <= shownMilestoneValue.value) {
        console.log('[奖励弹窗] 已弹出过更高里程碑，跳过', {
          current: highest.value,
          shown: shownMilestoneValue.value,
        });
        pendingRewards.value = [];
        return;
      }

      // 记录本次弹出的里程碑值
      shownMilestoneValue.value = highest.value;

      console.log('[奖励弹窗] 里程碑奖励', {
        days: highest.value,
        reward: highest.reward,
      });

      onMilestoneReward?.(highest.value, highest.reward || 0);
    } else {
      // 打卡奖励：如果已有任何里程碑弹出过，则不再弹出打卡奖励
      if (shownMilestoneValue.value > 0) {
        console.log('[奖励弹窗] 已弹出过里程碑奖励，跳过打卡奖励');
        pendingRewards.value = [];
        return;
      }

      onCheckinReward?.(highest.reward || 0);
    }

    // 清空待处理列表
    pendingRewards.value = [];
  }

  return {
    pendingRewards,
    shownMilestoneValue,
    collectPendingRewards,
    showHighestPriorityReward,
    hasShownRewardPopup,
    markRewardPopupShown,
  };
}

export default useRewardPopup;
